import type {
  ClientGeoArea,
  ClientGPSLocation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View
} from 'react-native';
import { Button, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { useGetNearestGeoArea } from '@/features/api/hooks/shared-app/useGetNearestGeoArea';
import { useGetNearestGeoAreas } from '@/features/api/hooks/shared-app/useGetNearestGeoAreas';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import {
  INITIAL_RANGE,
  type GeoAreaSelectionFeature
} from '@/features/feature-management/types/geoAreaSelectionFeature';
import { GeoAreaChipList } from '@/features/geo-area/components/GeoAreaChipList';
import { GeoSearchBar } from '@/features/geo-area/components/GeoSearchBar';
import { GeoAreaMap } from '@/features/geo-area/components/map/GeoAreaMap';
import {
  RANGE_SLIDER_HEIGHT,
  RangeSlider
} from '@/features/geo-area/components/RangeSlider';
import { GeoAreaSearch } from '@/features/geo-area/components/subscription/GeoAreaSearch';
import { SubscriptionMapContent } from '@/features/geo-area/components/subscription/SubscriptionMapContent';
import {
  MAP_HEIGHT_RATIO,
  MAP_HEIGHT_RATIO_STICKY,
  getMapHeight
} from '@/features/geo-area/constants/map';
import { distance, getMaxDistance } from '@/features/geo-area/utils/gps';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, bottom = 0) =>
  StyleSheet.create({
    flexRoot: { flex: 1 },
    wrapper: {
      gap: 32,
      paddingHorizontal: 16,
      paddingBottom: 32 + bottom
    },
    wrapperSticky: {
      gap: 32,
      paddingHorizontal: 16,
      paddingBottom: 32
    },
    footer: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 16 + bottom,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    title: { textAlign: 'center', paddingBottom: 32 },
    description: { textAlign: 'center', paddingBottom: 4, fontWeight: 'bold' },
    // Note: the wrapper padding was causing the thumb to be cut off at min/max, thus the negative margin and view padding
    map: {
      marginHorizontal: -16,
      paddingHorizontal: 16,
      borderRadius: 12,
      overflow: 'hidden'
    },
    mapTitle: { paddingBottom: 6, textAlign: 'center', fontWeight: 'bold' }
  });

type Props = {
  labels: {
    title?: string;
    description?: string;
    searchPlaceholder: string;
    map?: string;
    button: string;
  };
  disableButton?: boolean;
  /**
   * Renders the submit button in a fixed footer below the scrollable content
   * instead of as the last scrollable element. Intended for bottom sheets or
   * full-screen steps where long content (e.g. the chip list) would otherwise
   * push the button out of view.
   */
  stickyButton?: boolean;
  /**
   * When `stickyButton` is on, routes the scroll container through
   * BottomSheetScrollView for correct gesture negotiation with a parent
   * `@gorhom/bottom-sheet`. Set only when rendered inside one.
   */
  inBottomSheet?: boolean;
  selectedGeoAreaId?: ClientGeoArea['id'];
  selectedGeoAreaIds?: ClientGeoArea['id'][];
  onChange?: (id?: ClientGeoArea['id'], ids?: ClientGeoArea['id'][]) => void;
};

export const GeoAreaSubscription: FC<Props> = ({
  labels,
  disableButton,
  stickyButton = false,
  inBottomSheet = false,
  selectedGeoAreaId,
  selectedGeoAreaIds = [],
  onChange
}) => {
  const theme = useAppTheme();
  const { bottom } = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, bottom), [bottom, theme]);

  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'screens.geo-area-subscription.preview'
  });

  const { getFeature } = useFeature();
  const { featureValues } = getFeature<GeoAreaSelectionFeature>(
    FeaturesEnum.GeoAreaSelection
  );
  const { maxRadiusMeter, defaultRadiusMeter } = featureValues;

  const [range, setRange] = useState<number>(defaultRadiusMeter);
  const [initialRange, setInitialRange] = useState<number>(defaultRadiusMeter);
  const [isGeoAreaChanged, setIsGeoAreaChanged] = useState<boolean>(false);

  // #region GPS-Location
  const [position, setPosition] = useState<ClientGPSLocation>();
  const { data: nearestGeoArea, isFetching: isNearestGeoAreaFetching } =
    useGetNearestGeoArea(position);

  const handleLocatePosition = useCallback(
    (position: ClientGPSLocation) => {
      setPosition(position);
      setRange(defaultRadiusMeter);
      setInitialRange(defaultRadiusMeter);
    },
    [defaultRadiusMeter]
  );
  // #endregion

  // #region selected GeoArea
  const [selectedId, setSelectedId] = useState<ClientGeoArea['id']>();
  useEffect(() => {
    if (!isEmptyOrNull(selectedGeoAreaId)) setSelectedId(selectedGeoAreaId);
  }, [selectedGeoAreaId]);

  useEffect(() => {
    if (nearestGeoArea) {
      if (nearestGeoArea.id === selectedId) return;
      setSelectedId(nearestGeoArea.id);
      setIsGeoAreaChanged(true);
    }
  }, [nearestGeoArea, selectedId]);

  const { data: geoArea, isFetching: isGeoAreaFetching } =
    useGetGeoArea(selectedId);

  const isLoading = useMemo(
    () => isNearestGeoAreaFetching || isGeoAreaFetching,
    [isGeoAreaFetching, isNearestGeoAreaFetching]
  );

  // #endregion

  // #region selected GeoAreas and map (range and unselected)
  const [selectedIds, setSelectedIds] = useState<ClientGeoArea['id'][]>([]);

  useEffect(() => {
    if (hasItems(selectedGeoAreaIds)) setSelectedIds(selectedGeoAreaIds);
  }, [selectedGeoAreaIds]);

  const [unselectedIds, setUnselectedIds] = useState<ClientGeoArea['id'][]>([]);

  const { data: nearestGeoAreas } = useGetNearestGeoAreas(
    geoArea?.center,
    maxRadiusMeter / 1000
  );

  // GeoAreas which should be visible withing the range
  const visibleGeoAreas = useMemo(() => {
    const geoAreas =
      nearestGeoAreas?.filter(
        ({ id, leaf, center }) =>
          leaf === true &&
          id !== geoArea?.id &&
          distance(geoArea?.center, center) < range
      ) ?? [];

    return geoAreas;
  }, [geoArea?.center, geoArea?.id, nearestGeoAreas, range]);

  const selectedGeoAreas = useMemo(
    () => visibleGeoAreas.filter(({ id }) => selectedIds.includes(id)),
    [selectedIds, visibleGeoAreas]
  );

  useEffect(() => {
    setUnselectedIds((prevValues) =>
      prevValues.filter((id) =>
        visibleGeoAreas.some((geoArea) => geoArea.id === id)
      )
    );
  }, [visibleGeoAreas]);

  // Update selected GeoAreas when the visible GeoAreas change while respecting the unselected list
  useEffect(() => {
    if (hasItems(visibleGeoAreas)) {
      const newSelectedGeoAreaIds = visibleGeoAreas
        .map(({ id }) => id)
        .filter((id) => !unselectedIds.includes(id));
      setSelectedIds(newSelectedGeoAreaIds);
    } else setSelectedIds([]);
  }, [visibleGeoAreas, unselectedIds]);

  // Calculate initial range and unselected GeoAreas based on the passed prop selectedGeoAreaIds
  useEffect(() => {
    // If geo area is changed do nothing
    if (isGeoAreaChanged) return;

    if (geoArea && hasItems(nearestGeoAreas)) {
      // Set range to a very low number if there are no selected geo areas and only the selected geo area is passed
      if (selectedGeoAreaIds.length === 0) {
        setRange(0);
        setInitialRange(0);
      } else {
        const selected = nearestGeoAreas.filter(({ id }) =>
          selectedGeoAreaIds.includes(id)
        );

        // Todo: the max distance calculation seems to miss some of the selected geo areas
        const initialRange =
          (getMaxDistance(geoArea.center, selected) ?? INITIAL_RANGE) * 1.025;
        // set range to calculated max distance of center and selected geo areas
        setRange(initialRange);
        setInitialRange(initialRange);

        const unselected =
          nearestGeoAreas
            ?.filter(
              ({ id, leaf, center }) =>
                leaf === true &&
                id !== geoArea?.id &&
                distance(geoArea?.center, center) < initialRange &&
                !selectedGeoAreaIds.includes(id)
            )
            .map(({ id }) => id) ?? [];

        setUnselectedIds(unselected);
      }
    }
  }, [geoArea, isGeoAreaChanged, nearestGeoAreas, selectedGeoAreaIds]);

  const handleSearchSelect = useCallback(
    (id?: string) => {
      if (id === selectedId) return;

      // geo area is changed -> set range to initial range
      setRange(INITIAL_RANGE);
      setInitialRange(INITIAL_RANGE);
      setSelectedId(id);
      setIsGeoAreaChanged(true);
    },
    [selectedId]
  );

  /**
   * Handle GeoArea clicks on the map
   * Add or remove the id from the selected/unselected list
   */
  const handleGeoAreaClick = useCallback(
    (id: ClientGeoArea['id']) => {
      if (id === geoArea?.id) return;
      setSelectedIds((prevIds) => {
        if (prevIds?.includes(id))
          return prevIds.filter((includedId) => includedId !== id);
        else return [...(prevIds ?? []), id];
      });
      setUnselectedIds((prevIds) => {
        if (prevIds?.includes(id))
          return prevIds.filter((includedId) => includedId !== id);
        else return [...(prevIds ?? []), id];
      });
    },
    [geoArea?.id]
  );
  // #endregion

  // Clears states
  const handleClear = useCallback(() => {
    setPosition(undefined);
    setSelectedId(undefined);
    setSelectedIds([]);
    setUnselectedIds([]);
    onChange?.();
  }, [onChange]);

  const handleSave = useCallback(() => {
    onChange?.(geoArea?.id, selectedIds);
  }, [geoArea?.id, onChange, selectedIds]);

  // #region View

  // Enables save button
  const canSave = useMemo(
    () => !isEmptyOrNull(geoArea?.id) && !disableButton,
    [disableButton, geoArea?.id]
  );

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const handleOpenSearch = () => setShowSearch(true);
  const handleCloseSearch = () => setShowSearch(false);

  const { height } = useWindowDimensions();

  const mapHeight = getMapHeight(
    height,
    stickyButton ? MAP_HEIGHT_RATIO_STICKY : MAP_HEIGHT_RATIO
  );
  const submitButton = (
    <Button disabled={!canSave} mode="contained" onPress={handleSave}>
      {labels.button}
    </Button>
  );

  const content = (
    <>
      <View>
        {!isEmptyOrNull(labels.title) && (
          <Text variant="headlineLarge" style={styles.title}>
            {labels.title}
          </Text>
        )}
        {!isEmptyOrNull(labels.description) && (
          <Text variant="titleMedium" style={styles.description}>
            {labels.description}
          </Text>
        )}
        <GeoSearchBar
          label={geoArea?.name}
          placeholder={labels.searchPlaceholder}
          isLoading={isLoading}
          onLocate={handleLocatePosition}
          onClear={handleClear}
          onPress={handleOpenSearch}
        />
      </View>
      {geoArea && (
        <View>
          {!isEmptyOrNull(labels.map) && (
            <Text variant="titleMedium" style={styles.mapTitle}>
              {labels.map}
            </Text>
          )}
          <View
            style={[styles.map, { minHeight: mapHeight + RANGE_SLIDER_HEIGHT }]}
          >
            <View style={{ height: mapHeight }}>
              <GeoAreaMap onGeoAreaClicked={handleGeoAreaClick}>
                <SubscriptionMapContent
                  homeGeoArea={geoArea}
                  visibleGeoAreas={visibleGeoAreas}
                  selectedIds={selectedIds}
                  range={range}
                />
              </GeoAreaMap>
            </View>

            {
              // Note: if the slider is shown, the modal is not rendered correctly
              !showSearch && (
                <RangeSlider
                  initialRange={initialRange}
                  maxRange={maxRadiusMeter}
                  onRangeChange={setRange}
                />
              )
            }
          </View>
        </View>
      )}
      {geoArea && hasItems(selectedGeoAreas) && (
        <GeoAreaChipList
          areas={selectedGeoAreas}
          countLabel={t('count', { count: selectedGeoAreas.length })}
        />
      )}
    </>
  );

  if (stickyButton) {
    // Inside a bottom sheet the BottomSheetScrollView is required for gesture
    // negotiation (a plain ScrollView blocks scrolling entirely). Outside one
    // it would throw via useBottomSheetInternal, so fall back to ScrollView.
    const ScrollComponent = inBottomSheet ? BottomSheetScrollView : ScrollView;
    return (
      <>
        <GeoAreaSearch
          visible={showSearch}
          onSelect={handleSearchSelect}
          onDismiss={handleCloseSearch}
        />
        <View
          style={[
            styles.flexRoot,
            { backgroundColor: theme.colors.surfaceContainerLow }
          ]}
        >
          <ScrollComponent
            style={styles.flexRoot}
            contentContainerStyle={styles.wrapperSticky}
          >
            {content}
          </ScrollComponent>
          <View style={styles.footer}>{submitButton}</View>
        </View>
      </>
    );
  }

  return (
    <>
      <GeoAreaSearch
        visible={showSearch}
        onSelect={handleSearchSelect}
        onDismiss={handleCloseSearch}
      />
      <StateView
        backgroundColor={theme.colors.surfaceContainerLow}
        contentContainerStyle={styles.wrapper}
      >
        {content}
        {submitButton}
      </StateView>
    </>
  );
  // #endregion
};
